import { observer } from 'mobx-react-lite';
import { getSnapshot } from 'mobx-state-tree';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import Box from 'ui-box';
import {
  Button,
  EditableText,
  FormField,
  majorScale,
  Menu,
  MenuDivider,
  MenuItem,
  NumberInput,
  Popover,
  Spinner,
  Table,
  useToast,
} from '../../npaper';
import { formatUnit } from '../../npaper/FormattedUnit';
import {
  IPurchaseOrderItem,
  ISupplierItem,
  Unit,
  useStores,
} from '../../stores';
import {
  IPurchaseOrder,
  PurchaseOrderStatus,
} from '../../stores/PurchaseOrderStore';
import { ProductSupplierSelect } from '../products/ProductSupplierSelect';
import { ProductThumbnail } from '../products/ProductThumbnail';
import { SaleThumbnail } from '../sales';
import { StatusBar } from './StatusBar';

export interface IPurchaseOrderLinesTableProps {
  purchaseOrder: IPurchaseOrder;
}

export const PurchaseOrderLinesTable: React.FC<IPurchaseOrderLinesTableProps> =
  observer(({ purchaseOrder }) => {
    const intl = useIntl();
    const { purchaseOrderItemStore } = useStores();
    const toaster = useToast();
    const [items, setItems] = useState<IPurchaseOrderItem[]>();

    useEffect(() => {
      let isSub = true;
      purchaseOrderItemStore
        .getAll({ filter: { purchase_order: purchaseOrder.id } })
        .then(({ items: lines }) => isSub && setItems(lines));
      return () => {
        isSub = false;
      };
    }, [purchaseOrder, purchaseOrderItemStore]);

    const [addingLine, setAddingLine] = useState(false);
    const addSupplierItem = async (supplierItem: ISupplierItem) => {
      if (!purchaseOrder) return;
      setAddingLine(true);
      try {
        await purchaseOrderItemStore.createItem({
          purchase_order: purchaseOrder.id,
          supplier_item: supplierItem.id,
          quantity: 1,
        });
      } catch (err) {
        toaster.showDefaultError(err);
      }
      setAddingLine(false);
    };

    const AddProductButton = purchaseOrder.status ===
      PurchaseOrderStatus.DRAFT && (
      <Box display="flex">
        <ProductSupplierSelect
          showTitle
          filter={{ supplier: getSnapshot(purchaseOrder).supplier }}
          onSelectSupplierProductItem={(supplierItem) =>
            addSupplierItem(supplierItem)
          }
        >
          <Button iconLeft="add" isLoading={addingLine}>
            <FormattedMessage defaultMessage="Add product" />
          </Button>
        </ProductSupplierSelect>
      </Box>
    );

    return !items ? (
      <Box marginBottom={majorScale(2)}>
        <Spinner size={16} />
      </Box>
    ) : items.length === 0 ? (
      <Box marginBottom={majorScale(2)} marginTop={majorScale(2)}>
        <p>Ingen linjer tilføjet</p>
        <Box marginTop={majorScale(1)}>{AddProductButton}</Box>
      </Box>
    ) : (
      <Table marginTop={majorScale(-2)}>
        <thead>
          <tr>
            <th className="sticky">Produkt</th>
            <th>Lev. vare nr.</th>
            <th>Relateret salg</th>
            <th className="right">Kostpris (styk)</th>
            <th className="right">Antal</th>
            <th className="right">Kostpris</th>
            <th>Status</th>
            <th style={{ width: '40px' }}></th>
            {purchaseOrder.status === PurchaseOrderStatus.DRAFT && (
              <th style={{ width: '40px' }}></th>
            )}
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => (
            <tr key={item.id}>
              <td className="sticky" style={{ maxWidth: '400px' }}>
                {item.product ? (
                  <ProductThumbnail
                    showName={true}
                    showSKU
                    maxWidth="100%"
                    product={item.product}
                  />
                ) : (
                  <Spinner size={16} />
                )}
              </td>
              <td className="font-mono">
                {item.supplier_item ? (
                  item.supplier_item.supplier_number
                ) : (
                  <Spinner size={16} />
                )}
              </td>
              <td>
                {item.related_sales.map((sale, i) =>
                  sale ? (
                    <SaleThumbnail
                      key={sale.id}
                      sale={sale}
                      marginRight={majorScale(2)}
                      showNumber={true}
                    />
                  ) : (
                    <Spinner size={16} key={`index-${i}`} />
                  )
                )}
                {item.related_sales.length === 0 && (
                  <span className="muted">Ingen relaterede</span>
                )}
              </td>
              <td className="right font-mono">
                <EditableText
                  size={13}
                  type="number"
                  display="inline-flex"
                  defaultValue={item.cost_price}
                  value={item.cost_price / item.quantity}
                  onConfirm={(s, n) => {
                    if (n !== undefined && !isNaN(n)) {
                      const old = item.cost_price;
                      item.setUnitCostPrice(n);
                      purchaseOrderItemStore
                        .updateItem(item, { cost_price: item.cost_price })
                        .catch((e) => {
                          toaster.showDefaultError(e);
                          item.setCostPrice(old);
                        });
                    }
                  }}
                  unit={item.cost_price_currency}
                  minWidth="140px"
                />
              </td>
              <td className="right font-mono" style={{ width: '110px' }}>
                <EditableText
                  size={9}
                  type="number"
                  readOnly={purchaseOrder.status !== PurchaseOrderStatus.DRAFT}
                  value={item.quantity}
                  maximumFractionDigits={4}
                  minimumFractionDigits={0}
                  minWidth="80px"
                  unit={formatUnit(
                    intl,
                    item.product ? item.product.unit : Unit.PCS
                  )}
                  onConfirm={(s, n) => {
                    if (n !== undefined && !isNaN(n)) {
                      const old = item.quantity;
                      item.setQuantity(n);
                      purchaseOrderItemStore
                        .updateItem(item, {
                          quantity: n,
                        })
                        .catch((e) => {
                          toaster.showDefaultError(e);
                          item.setQuantity(old);
                        });
                    }
                  }}
                />
              </td>
              <td className="right font-mono">
                <EditableText
                  size={13}
                  type="number"
                  display="inline-flex"
                  defaultValue={item.cost_price}
                  value={item.cost_price}
                  onConfirm={(s, n) => {
                    if (n !== undefined && !isNaN(n)) {
                      const old = item.cost_price;
                      item.setCostPrice(n);
                      purchaseOrderItemStore
                        .updateItem(item, { cost_price: n })
                        .catch((e) => {
                          toaster.showDefaultError(e);
                          item.setCostPrice(old);
                        });
                    }
                  }}
                  unit={item.cost_price_currency}
                  minWidth="140px"
                />
              </td>
              <td>
                <p className="small">
                  {item.quantity_completed} af {item.quantity}
                </p>
                <StatusBar
                  success={item.quantity_completed / item.quantity}
                  danger={
                    item.quantity_completed / item.quantity +
                    item.quantity_cancelled / item.quantity
                  }
                />
              </td>
              <td>
                <Popover
                  placement="bottom-end"
                  content={(close) => (
                    <UpdatePurchaseOrderItemStatusMenu
                      item={item}
                      close={close}
                    />
                  )}
                >
                  <Button iconLeft="edit" minimal />
                </Popover>
              </td>
              {purchaseOrder.status === PurchaseOrderStatus.DRAFT && (
                <td>
                  <Button
                    iconLeft="trash"
                    intent="danger"
                    minimal
                    onClick={() =>
                      purchaseOrderItemStore
                        .deleteItem(item)
                        .catch(toaster.showDefaultError)
                    }
                  />
                </td>
              )}
            </tr>
          ))}
        </tbody>
        {items.length > 0 && (
          <tfoot>
            <tr>
              <th colSpan={5}>{AddProductButton}</th>
              <Box
                is="th"
                className="font-mono medium right"
                verticalAlign="top"
              >
                {/* TODO: Use correct currency */}
                <FormattedNumber
                  format="currency"
                  value={items.reduce((p, n) => p + n.cost_price, 0)}
                />
              </Box>
              <Box is="th" className="right" fontWeight="normal">
                <p>
                  <span className="font-mono">
                    {items.reduce((p, n) => p + n.quantity_completed, 0)}
                  </span>{' '}
                  færdige
                </p>
                <p>
                  <span className="font-mono">
                    {items.reduce((p, n) => p + n.quantity_cancelled, 0)}
                  </span>{' '}
                  annulleret
                </p>
              </Box>
              <th></th>
            </tr>
          </tfoot>
        )}
      </Table>
    );
  });

export const UpdatePurchaseOrderItemStatusMenu = ({
  item,
  close,
}: {
  item: IPurchaseOrderItem;
  close: () => void;
}) => {
  const { purchaseOrderItemStore, purchaseOrderStore } = useStores();
  const toaster = useToast();
  const [done, setDone] = useState(item.quantity_completed);
  const [cancelled, setCancelled] = useState(item.quantity_cancelled);

  const [saving, setSaving] = useState(false);

  const updateLineStatus = async () => {
    setSaving(true);
    try {
      await purchaseOrderItemStore.updateItem(item, {
        quantity_completed: done,
        quantity_cancelled: cancelled,
      });
      purchaseOrderStore.getItemWithQueue(
        getSnapshot(item).purchase_order as string | number,
        true
      );
    } catch (err) {
      if (err.response && err.response.data['consumers']) {
        toaster.addToast({
          message: err.response.data['consumers'][0],
          intent: 'danger',
          timeout: 6000,
        });
      } else {
        toaster.showDefaultError(err);
      }
    }
    setSaving(false);
    close();
  };

  return (
    <Menu>
      <FormField
        label="Modtaget"
        marginX={majorScale(2)}
        marginTop={majorScale(1)}
        marginBottom={majorScale(2)}
      >
        <NumberInput
          width="140px"
          placeholder="Skriv antal"
          defaultValue={item.quantity_completed}
          min={0}
          max={item.quantity - cancelled}
          onChange={setDone}
        />
      </FormField>
      <FormField
        label="Annulleret"
        marginX={majorScale(2)}
        marginTop={majorScale(1)}
        marginBottom={majorScale(2)}
      >
        <NumberInput
          width="140px"
          placeholder="Skriv antal"
          defaultValue={item.quantity_cancelled}
          min={0}
          max={item.quantity - done}
          onChange={setCancelled}
        />
      </FormField>
      <Box
        is="p"
        marginX={majorScale(2)}
        marginTop={majorScale(1)}
        marginBottom={majorScale(2)}
      >
        Rest:{' '}
        <span className="font-mono">
          <FormattedNumber
            format="default"
            value={item.quantity - done - cancelled}
          />
        </span>
      </Box>
      <MenuDivider />
      <MenuItem
        intent="info"
        text="Gem"
        onClick={() => updateLineStatus()}
        disabled={saving}
      />
    </Menu>
  );
};
